import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress } from '@mui/material';
import Layout from '../components/Layout';
import banner from '../assets/tpp-claims-banner.jpg';
import {
  ClaimStatusResponse,
  getClaimStatusByToken,
  sendClaimMessage,
} from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import { useSnackbar } from '../contexts/SnackbarContext';

const formatCurrency = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (!Number.isFinite(num)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ClaimStatusPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const { showSuccess, showError } = useSnackbar();
  const queryClaimToken = new URLSearchParams(location.search).get('claimToken') || '';
  const resolvedToken = token || queryClaimToken;

  const [isLoading, setIsLoading] = useState(true);
  const [statusData, setStatusData] = useState<ClaimStatusResponse | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!resolvedToken) {
      setError('No claim token provided.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError('');

    getClaimStatusByToken(resolvedToken)
      .then((data) => {
        if (!cancelled) {
          setStatusData(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err) || 'Unable to load claim status.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedToken]);

  const handleSubmitMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || !resolvedToken) return;

    setIsSending(true);
    try {
      const newItem = await sendClaimMessage(resolvedToken, trimmed);
      setStatusData((prev) =>
        prev
          ? { ...prev, activity: [...prev.activity, newItem] }
          : prev
      );
      setMessage('');
      showSuccess('Your message has been sent.');
    } catch (err) {
      showError(getErrorMessage(err) || 'Unable to send message.');
    } finally {
      setIsSending(false);
    }
  };

  const claim = statusData?.claim;
  const isDraftClaim = claim?.status?.toUpperCase() === 'DRAFT';
  const isIncompleteClaim = claim?.status?.toUpperCase() === 'INCOMPLETE';
  const todoHtml = statusData?.todo_html?.trim() || '';
  const hasTodoList = todoHtml !== '' || (statusData?.todo_list.length ?? 0) > 0;
  const showActionPanel = isDraftClaim || isIncompleteClaim;
  const inventoryPageUrl = claim?.token
    ? `/damages?claimToken=${encodeURIComponent(claim.token)}&from=claim-status`
    : null;

  return (
    <Layout>
      <div className="banner banner--claim-status">
        <img src={banner} alt="TPP Claims Banner" />
        <div className="banner-content">
          <h1>Claim Status</h1>
          <h2>Tenant Protection Plan</h2>
          <p className="banner-content__lede--claim-status">
            Track your claim, view messages, and complete your to do list.
          </p>
        </div>
      </div>

      <div className="claim-status-page">
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : claim ? (
          <div className="claim-status-layout">
            <div className="claim-status-main">
              <header className="claim-status-header">
                <h1 className="claim-status-title">Claim {claim.claim_number}</h1>
                <p className="claim-status-created">
                  Created by {claim.created_by} on {formatDateTime(claim.created_at)}
                </p>
              </header>

              <div className="claim-status-summary">
                <div className="claim-status-summary-item">
                  <span className="claim-status-summary-label">Status</span>
                  <span className={`claim-status-summary-value claim-status-summary-status claim-status-badge--${claim.status.toLowerCase()}`}>
                    {claim.status_label}
                  </span>
                </div>
                <div className="claim-status-summary-item">
                  <span className="claim-status-summary-label">Unit Number</span>
                  <span className="claim-status-summary-value">{claim.facility_unit || '—'}</span>
                </div>
                <div className="claim-status-summary-item">
                  <span className="claim-status-summary-label">Coverage Limit</span>
                  <span className="claim-status-summary-value">{formatCurrency(claim.coverage_limit)}</span>
                </div>
                <div className="claim-status-summary-item">
                  <span className="claim-status-summary-label">Claim Total</span>
                  <span className="claim-status-summary-value">{formatCurrency(claim.claim_total)}</span>
                </div>
              </div>

              <button
                type="button"
                className="claim-status-details-toggle"
                onClick={() => setShowDetails((v) => !v)}
                aria-expanded={showDetails}
              >
                {showDetails ? 'Hide Details' : 'Show Details'} {showDetails ? '▲' : '▼'}
              </button>

              {showDetails && (
                <div className="claim-status-details">
                  <p><strong>Peril:</strong> {claim.peril || '—'}</p>
                  <p><strong>Status:</strong> {claim.status_label}</p>
                </div>
              )}

              <section className="claim-status-activity">
                <h2 className="claim-status-section-title">Activity</h2>
                {statusData.activity.length === 0 ? (
                  <p className="claim-status-empty">No messages yet. Use the form below to contact your claim agent.</p>
                ) : (
                  <ul className="claim-status-messages">
                    {statusData.activity.map((item) => (
                      <li key={item.id} className="claim-status-message">
                        <div className="claim-status-message-meta">
                          <span className={`claim-status-message-icon ${item.is_from_tenant ? 'claim-status-message-icon--out' : 'claim-status-message-icon--in'}`}>
                            {item.is_from_tenant ? '↩' : '✉'}
                          </span>
                          <span className="claim-status-message-sender">{item.sender_label}</span>
                          <span className="claim-status-message-time">{formatDateTime(item.created_at)}</span>
                        </div>
                        <p className="claim-status-message-body">{item.note}</p>
                      </li>
                    ))}
                  </ul>
                )}

                <form className="claim-status-compose" onSubmit={handleSubmitMessage}>
                  <p className="claim-status-compose-hint">
                    Send us a message about your claim. Enter your message here and submit it.
                  </p>
                  <textarea
                    className="claim-status-textarea"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    placeholder="Enter your message..."
                    disabled={isSending}
                  />
                  <button
                    type="submit"
                    className="button button-primary claim-status-submit"
                    disabled={isSending || !message.trim()}
                  >
                    {isSending ? 'Sending…' : 'Submit'}
                  </button>
                </form>
              </section>
            </div>

            <aside className="claim-status-sidebar">
              <section className="claim-status-panel">
                <h2 className="claim-status-section-title">Status</h2>
                <p className={`claim-status-badge claim-status-badge--${claim.status.toLowerCase()}`}>
                  {claim.status_label}
                </p>
                <p className="claim-status-description">{claim.status_description}</p>
                <dl className="claim-status-meta-list">
                  <div>
                    <dt>Agent</dt>
                    <dd>{claim.agent_name}</dd>
                  </div>
                  <div>
                    <dt>Peril</dt>
                    <dd>{claim.peril || '—'}</dd>
                  </div>
                </dl>
              </section>

              {showActionPanel && (
                <section className="claim-status-panel claim-status-panel--checklist">
                  <h2 className="claim-status-section-title">Claim Check List</h2>
                  <div className="claim-status-checklist-body">
                    {todoHtml ? (
                      <div
                        className="claim-status-todo claim-status-todo--html"
                        dangerouslySetInnerHTML={{ __html: todoHtml }}
                      />
                    ) : hasTodoList ? (
                      <ol className="claim-status-todo">
                        {statusData.todo_list.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ol>
                    ) : (
                      <p className="claim-status-empty">No items at this time.</p>
                    )}
                  </div>
                  {(isDraftClaim || (isIncompleteClaim && inventoryPageUrl)) && (
                    <div className="claim-status-upload-actions">
                      {isDraftClaim && (
                        <a
                          href={claim.resume_url}
                          className="button button-primary claim-status-upload-btn"
                        >
                          Complete your claim
                        </a>
                      )}
                      {isIncompleteClaim && inventoryPageUrl && (
                        <a
                          href={inventoryPageUrl}
                          className="button button-primary claim-status-upload-btn"
                        >
                          Upload Documents/Pictures
                        </a>
                      )}
                    </div>
                  )}
                </section>
              )}
            </aside>
          </div>
        ) : null}
      </div>
    </Layout>
  );
};

export default ClaimStatusPage;
